import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { setLocale, getLocale, type Locale } from '$lib/i18n';
import { cn } from '$lib/lib/cn';

// Language toggle. Sits next to ThemeToggle in the TopBar so the same area is
// consistently the place users go for global preferences. Two-state toggle —
// EN ↔ DA — clicking flips to the other language. The active language code
// is the visible label so the analyst can see at a glance which language
// they're in (we considered a flag-icon dropdown, but a code-only toggle
// keeps the bar tight and avoids the flag-vs-language ambiguity Nordic teams
// often flag).

const LABEL: Record<Locale, string> = {
  en: 'EN',
  da: 'DA'
};

export function LanguageSwitcher({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const current = getLocale();
  const next: Locale = current === 'en' ? 'da' : 'en';
  const nextLabel = next === 'da' ? t('languageSwitcher.danish') : t('languageSwitcher.english');
  return (
    <button
      type="button"
      onClick={() => setLocale(next)}
      aria-label={t('languageSwitcher.switchTo', { language: nextLabel })}
      title={t('languageSwitcher.switchTo', { language: nextLabel })}
      data-testid="language-switcher"
      data-locale={i18n.language}
      className={cn(
        'inline-flex h-8 items-center gap-1 rounded-sm border border-rule-strong bg-surface px-2 text-2xs font-semibold text-fg-muted',
        'hover:bg-surface-2 hover:text-fg transition-colors duration-fast',
        className
      )}
    >
      <Languages size={12} aria-hidden />
      <span className="num">{LABEL[current]}</span>
    </button>
  );
}
